<template>
  <li
    :class="{
      'no-accordion': !this.showAccordion,
      'no-borders': !this.borders,
      nested: this.nested,
    }"
    class="accordion__item"
    @keyup.prevent.down="nextHeading"
    @keyup.prevent.up="prevHeading"
    @keyup.prevent.esc="close"
  >
    <div
      v-if="showAccordion"
      :class="{ 'accordion__item--expanded': expanded }"
    >
      <div class="accordion__title">
        <!-- aria-expanded attribute needs to be in html even when false, so :aria-expanded="expanded" isn't enough -->
        <button
          :aria-controls="`accordion-${index}`"
          :aria-expanded="expanded ? 'true' : 'false'"
          @click.prevent="toggle"
          class="accordion__btn"
        >
          <component :is="tag" v-if="buttonText" class="accordion__heading">
            <div v-html="buttonText" />
          </component>

          <div
            v-if="toggleText"
            :class="{ 'accordion--toggled': expanded }"
            class="accordion__toggle-icon-text"
            :aria-hidden="!expanded"
          >
            <p>{{ expanded ? "Close" : toggleText }}</p>
            <svg class="icon">
              <use
                xlink:href="/app/themes/ahc/src/assets/images/icons.svg#chevron-down"
              />
            </svg>
          </div>

          <div
            v-else
            :class="{ 'accordion--toggled': expanded }"
            class="accordion__toggle-icon"
            :aria-hidden="!expanded"
          />
        </button>
      </div>

      <collapse-transition>
        <div v-show="expanded">
          <div
            :id="`accordion-${index}`"
            :class="contentClass()"
            :aria-hidden="!expanded"
            class="accordion__content"
          >
            <slot name="text"></slot>
            <picture v-if="image" class="accordion__inset">
              <source :srcset="image.small" media="(max-width: 480px)" />
              <img
                class="accordion__inset"
                :src="image.medium"
                :alt="image.alt"
              />
            </picture>
            <slot name="blocklist"></slot>
          </div>
        </div>
      </collapse-transition>
    </div>

    <div v-else>
      <component :is="tag" class="accordion__heading">
        {{ title }}
      </component>
      <slot></slot>
    </div>
  </li>
</template>

<script>
import { CollapseTransition } from "vue2-transitions";

export default {
  name: "ahc-accordion",
  components: {
    CollapseTransition,
  },
  props: {
    title: {
      default: "Title",
      type: String,
    },
    subtitle: {
      default: "",
      type: String,
    },
    open: {
      default: false,
      type: Boolean,
    },
    mobileOnly: {
      default: false,
      type: Boolean,
    },
    html: {
      default: "Sample HTML",
      type: String,
    },
    tag: {
      type: String,
      default: "h3",
    },
    borders: {
      type: Boolean,
      default: true,
    },
    image: {
      type: Object,
    },
    hasBlocklist: {
      type: Boolean,
      default: false,
    },
    nested: {
      type: Boolean,
      default: false,
    },
    toggleText: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      expanded: false,
      index: Math.floor(Math.random() * Math.floor(1000)),
    };
  },
  computed: {
    showAccordion() {
      return (
        (this.$breakpoint.mdAndDown && this.mobileOnly) || !this.mobileOnly
      );
    },
    buttonText() {
      const inset = "";
      const title = this.subtitle
        ? `${this.title} <span>${this.subtitle}</span>`
        : this.title;
      return `${inset}<div>${title}</div>`;
    },
    showColumns() {
      return this.$parent.showColumns;
    },
  },
  mounted() {
    const vm = this;
    // should this item be open by default?
    vm.expanded = this.open;

    // if this accordion item hears a reset from the lms-accordion-group parent
    this.$parent.$on("resetAccordions", (idx) => {
      this.reset(idx);
    });
  },
  methods: {
    /**
     * Toggle this accordion and emit an event up to the lms-accordion-group parent
     * @param {boolean} emit
     */
    toggle(emit = true) {
      if (emit && this.$parent.single) {
        this.$parent.$emit("accordionToggled", this.index);
      }
      this.$emit("accordionToggled2", "test");
      this.expanded = !this.expanded;
    },
    close() {
      if (this.expanded) {
        this.toggle();
      }
    },
    /**
     * if this accordion item is open and does not match the ID of the active item, close it
     * @param {number} idx
     */
    reset(idx) {
      if ((idx !== this.index || this.index === "all") && this.expanded) {
        this.toggle(false);
      }
    },
    /**
     * Jump to next accordion heading
     */
    nextHeading() {
      const next = this.$el.nextElementSibling;
      if (next) {
        const heading = next.querySelector(".accordion__btn");
        if (heading) {
          heading.focus();
        }
      }
    },
    /**
     * Jump to previous accordion heading
     */
    prevHeading() {
      const prev = this.$el.previousElementSibling;
      if (prev) {
        const heading = prev.querySelector(".accordion__btn");
        if (heading) {
          heading.focus();
        }
      }
    },
    /**
     * Return class for this accordion item
     * @returns {{expanded: boolean, "accordion-group--2col": boolean}}
     */
    contentClass() {
      return {
        expanded: this.expanded,
        hasImage: this.image,
        blocklistOnly: !!this.$slots.text[0] && !this.image,
        hasBlocklist: this.hasBlocklist,
      };
    },
  },
};
</script>

<style lang="scss">
@import "accordion";
</style>
